'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import Link from 'next/link';
import { contactFormSchema, type ContactFormValues } from '@/lib/validators/lead.schema';
import { S3_CONFIG } from '@/lib/s3/config';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, Paperclip, X } from 'lucide-react';

// Values only — display labels are decoupled to `t('timezones.<v>')` etc. so the
// submitted/validated value stays a stable, locale-independent key.
const TIMEZONE_OPTIONS = ['eastern', 'central', 'mountain', 'pacific', 'other'] as const;

const GRADE_OPTIONS = [
  'kindergarten', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th',
  'middle_school', 'high_school',
] as const;

// Broad subject areas, not specific courses — courses come and go, but a parent
// may want to ask about a subject we aren't currently offering. Anything more
// specific can go in "Goals for Your Child" / "Additional Information".
const SUBJECT_OPTIONS = [
  'reading', 'writing', 'public_speaking', 'math', 'technology',
  '3d_modeling', 'esl', 'world_languages',
] as const;

// Credential upload constraints (mirror the server allow-list/cap).
const CREDENTIAL_ALLOWED_MIMES: string[] = [
  ...S3_CONFIG.allowedMimeTypes.document,
  ...S3_CONFIG.allowedMimeTypes.image,
];
const CREDENTIAL_ACCEPT = '.pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.gif';
const MAX_DOCUMENTS = 5;

type UploadedDocument = { s3Key: string; filename: string };

export function ContactForm({ variant = 'parent' }: { variant?: 'parent' | 'teacher' }) {
  const t = useTranslations('public.contact.form');
  const isTeacher = variant === 'teacher';
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Supporting-document upload state (teacher variant only). Each file is PUT
  // directly to S3 via a server-issued presigned URL; its {s3Key, filename} is
  // held here and merged into the submit payload. Up to MAX_DOCUMENTS files.
  const [documents, setDocuments] = useState<UploadedDocument[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      consentAcknowledged: undefined,
      inquiryType: variant,
    },
  });

  // Bind the hidden inquiryType discriminant to the variant so client-side
  // validation matches the server.
  useEffect(() => {
    setValue('inquiryType', variant);
  }, [variant, setValue]);

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Always clear the input so the same file can be re-selected after removal.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (documents.length >= MAX_DOCUMENTS) {
      toast.error(t('maxDocs', { max: MAX_DOCUMENTS }));
      return;
    }
    // Client-side MIME guard before requesting an upload URL. The size cap is
    // admin-configurable, so it's enforced server-side (the presign request
    // returns a clear error, surfaced below) rather than hardcoded here.
    if (!CREDENTIAL_ALLOWED_MIMES.includes(file.type)) {
      toast.error(t('unsupportedType'));
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      // 1. Request a presigned upload URL.
      const res = await fetch('/api/leads/credential-upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: file.name,
          contentType: file.type,
          fileSizeBytes: file.size,
        }),
      });
      if (!res.ok) {
        const result = await res.json().catch(() => null);
        toast.error(result?.error?.message ?? t('uploadFailed'));
        return;
      }
      const { uploadUrl, s3Key } = (await res.json()) as { uploadUrl: string; s3Key: string };

      // 2. PUT the file directly to S3 via the presigned URL.
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (ev) => {
          if (ev.lengthComputable) {
            setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed with status ${xhr.status}`));
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      setDocuments((prev) => [...prev, { s3Key, filename: file.name }]);
    } catch {
      toast.error(t('uploadFailed'));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }

  function removeDocument(index: number) {
    setDocuments((prev) => prev.filter((_, i) => i !== index));
  }

  const onSubmit = async (data: ContactFormValues) => {
    try {
      const payload = {
        ...data,
        inquiryType: variant,
        ...(isTeacher && documents.length > 0 ? { credentialDocuments: documents } : {}),
      };

      const res = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const result = await res.json();
        toast.error(result.error?.message ?? t('submitFailed'));
        return;
      }

      setSubmitted(true);
    } catch {
      toast.error(t('networkError'));
    }
  };

  if (submitted) {
    return (
      <div className="rounded-xl border bg-white p-8 text-center">
        <h3 className="mb-2 text-xl font-semibold text-eifo-primary">{t('thankYou')}</h3>
        <p className="mb-6 text-gray-600">
          {isTeacher ? t('thankYouTeacher') : t('thankYouParent')}
        </p>
        <Button asChild className="bg-eifo-orange text-white hover:bg-eifo-orange/90">
          <Link href="/courses">{t('browseCourses')}</Link>
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Honeypot */}
      <input
        {...register('honeypot')}
        aria-hidden="true"
        tabIndex={-1}
        autoComplete="off"
        className="absolute h-0 w-0 overflow-hidden opacity-0"
      />
      {/* Hidden inquiry discriminant — keeps client validation aligned with server. */}
      <input type="hidden" {...register('inquiryType')} value={variant} readOnly />

      <div className="grid gap-6 sm:grid-cols-2">
        {/* Name */}
        <div>
          <Label htmlFor="contact-name">
            {t('name')} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="contact-name"
            {...register('name')}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'contact-name-error' : undefined}
          />
          {errors.name && (
            <p id="contact-name-error" className="mt-1 text-xs text-red-500">
              {errors.name.message}
            </p>
          )}
        </div>

        {/* Child's Name — parent only */}
        {!isTeacher && (
          <div>
            <Label htmlFor="contact-child-name">{t('childName')}</Label>
            <Input id="contact-child-name" {...register('childName')} />
          </div>
        )}

        {/* Email */}
        <div>
          <Label htmlFor="contact-email">
            {t('email')} <span className="text-red-500">*</span>
          </Label>
          <Input
            id="contact-email"
            type="email"
            {...register('email')}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'contact-email-error' : undefined}
          />
          {errors.email && (
            <p id="contact-email-error" className="mt-1 text-xs text-red-500">
              {errors.email.message}
            </p>
          )}
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="contact-phone">{t('phone')}</Label>
          <Input
            id="contact-phone"
            type="tel"
            {...register('phone')}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? 'contact-phone-error' : undefined}
          />
          {errors.phone && (
            <p id="contact-phone-error" className="mt-1 text-xs text-red-500">
              {errors.phone.message}
            </p>
          )}
        </div>

        {/* Timezone */}
        <div>
          <Label htmlFor="contact-timezone">{t('timezone')}</Label>
          <select
            id="contact-timezone"
            {...register('timezone')}
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="">{t('selectTimezone')}</option>
            {TIMEZONE_OPTIONS.map((v) => (
              <option key={v} value={v}>
                {t(`timezones.${v}`)}
              </option>
            ))}
          </select>
        </div>

        {/* Grade Level — parent only */}
        {!isTeacher && (
          <div>
            <Label htmlFor="contact-grade">{t('childGrade')}</Label>
            <select
              id="contact-grade"
              {...register('gradeLevel')}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">{t('selectGrade')}</option>
              {GRADE_OPTIONS.map((v) => (
                <option key={v} value={v}>
                  {t(`grades.${v}`)}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Years of Experience — teacher only */}
        {isTeacher && (
          <div>
            <Label htmlFor="contact-years">{t('yearsExperience')}</Label>
            <Input
              id="contact-years"
              type="number"
              min={0}
              max={80}
              {...register('yearsOfExperience')}
              aria-invalid={!!errors.yearsOfExperience}
              aria-describedby={errors.yearsOfExperience ? 'contact-years-error' : undefined}
            />
            {errors.yearsOfExperience && (
              <p id="contact-years-error" className="mt-1 text-xs text-red-500">
                {errors.yearsOfExperience.message}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Interested Subjects — parent only */}
      {!isTeacher && (
        <div>
          <Label>{t('interestedSubjects')}</Label>
          <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {SUBJECT_OPTIONS.map((v) => (
              <label key={v} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  value={v}
                  {...register('interestedCourses')}
                  className="rounded border-gray-300"
                />
                {t(`subjects.${v}`)}
              </label>
            ))}
          </div>
          {errors.interestedCourses && (
            <p className="mt-1 text-xs text-red-500">{errors.interestedCourses.message}</p>
          )}
        </div>
      )}

      {/* Goals (parent) / Subjects (teacher) — both bound to `goals` */}
      <div>
        <Label htmlFor="contact-goals">
          {isTeacher ? t('goalsTeacher') : t('goalsParent')}
        </Label>
        <textarea
          id="contact-goals"
          {...register('goals')}
          rows={3}
          placeholder={isTeacher ? t('goalsTeacherPlaceholder') : undefined}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-invalid={!!errors.goals}
          aria-describedby={errors.goals ? 'contact-goals-error' : undefined}
        />
        {errors.goals && (
          <p id="contact-goals-error" className="mt-1 text-xs text-red-500">
            {errors.goals.message}
          </p>
        )}
      </div>

      {/* Additional Info (parent) / Message (teacher) — both bound to `additionalInfo` */}
      <div>
        <Label htmlFor="contact-info">{isTeacher ? t('infoTeacher') : t('infoParent')}</Label>
        <textarea
          id="contact-info"
          {...register('additionalInfo')}
          rows={3}
          className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-invalid={!!errors.additionalInfo}
          aria-describedby={errors.additionalInfo ? 'contact-info-error' : undefined}
        />
        {errors.additionalInfo && (
          <p id="contact-info-error" className="mt-1 text-xs text-red-500">
            {errors.additionalInfo.message}
          </p>
        )}
      </div>

      {/* Supporting documents — teacher only, optional, up to 5 files */}
      {isTeacher && (
        <div>
          <Label htmlFor="contact-credential">{t('supportingDocs')}</Label>
          <p className="mb-2 text-xs text-gray-500">
            {t('supportingDocsHelp', { max: MAX_DOCUMENTS })}
          </p>

          {documents.length > 0 && (
            <ul className="mb-3 space-y-2">
              {documents.map((doc, i) => (
                <li
                  key={doc.s3Key}
                  className="flex items-center gap-2 rounded-md border bg-[var(--eifo-bg-light)] px-3 py-2 text-sm"
                >
                  <Paperclip className="h-4 w-4 shrink-0 text-eifo-orange" />
                  <span className="truncate text-gray-700">{doc.filename}</span>
                  <button
                    type="button"
                    onClick={() => removeDocument(i)}
                    className="ml-auto text-gray-400 hover:text-red-500"
                    aria-label={t('removeDoc', { filename: doc.filename })}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {documents.length < MAX_DOCUMENTS && (
            <div className="flex items-center gap-3">
              <input
                id="contact-credential"
                ref={fileInputRef}
                type="file"
                accept={CREDENTIAL_ACCEPT}
                onChange={handleFileSelect}
                disabled={uploading}
                className="text-sm file:mr-3 file:rounded-md file:border-0 file:bg-eifo-primary file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-eifo-primary/90"
              />
              {uploading && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {uploadProgress}%
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Consent */}
      <div>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            {...register('consentAcknowledged')}
            className="mt-0.5 rounded border-gray-300"
            aria-invalid={!!errors.consentAcknowledged}
            aria-describedby={errors.consentAcknowledged ? 'contact-consent-error' : undefined}
          />
          <span className="text-gray-600">
            {isTeacher ? t('consentTeacher') : t('consentParent')}{' '}
            {/* A real <a> (interactive content) inside the <label> — clicking it
                navigates instead of toggling the checkbox, per the HTML spec. */}
            {t.rich('seePolicy', {
              link: (chunks) => (
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-eifo-primary"
                >
                  {chunks}
                </a>
              ),
            })}
          </span>
        </label>
        {errors.consentAcknowledged && (
          <p id="contact-consent-error" className="mt-1 text-xs text-red-500">
            {errors.consentAcknowledged.message}
          </p>
        )}
      </div>

      <Button
        type="submit"
        disabled={isSubmitting || uploading}
        className="w-full bg-eifo-orange text-white hover:bg-eifo-orange/90 sm:w-auto"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {t('submitting')}
          </>
        ) : isTeacher ? (
          t('submitApplication')
        ) : (
          t('sendMessage')
        )}
      </Button>
    </form>
  );
}
